import { useState } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import { signInErrorKey } from "@jackedlogs/shared";
import { Button, resolveTheme, TextField, type UiTheme } from "@jackedlogs/ui";
import { useAuth } from "./auth/AuthContext";
import type { AuthError } from "./auth/cognito";
import { useThemePalette } from "./theme";

// The web's sign-in screen. Born as a scaffold that only had to prove the
// react-native-web alias resolved end-to-end (View/Text/TextInput and the
// shared <Button> rendered on the DOM), and it kept the scaffold's copy and
// a11y long after it became the front door people actually sign in through.
// The legacy web's full SignInPage (forgot-password, confirm-code, validation)
// is still NOT ported here; those surfaces migrate later.
//
// Theme-aware: every colour comes from the resolved palette (WebThemeProvider
// wraps this screen), so the sign-in shell follows the chosen theme + light/dark
// instead of the old hardcoded dark-only palette (which also carried a drifted
// brand orange `#ff7a1a` ≠ canonical brand500).
//
// RW-23: the submit control was a hand-rolled <Pressable> + <Text>, which
// react-native-web renders as a bare `div`, so the front door of the whole web
// announced itself as TEXT and not as a command. The keyboard DID reach it
// (r-n-w puts `tabindex=0` on the div, and Enter fired); what it did not do was
// activate on SPACE, which only a real <button> gets from the browser. Measured
// before and after, `qa-harness.md §4` has the table. The shared <Button>
// already owns the role, the disabled/busy state and `webTapFloor` (the 44px
// floor r-n-w needs because its Pressable ignores hitSlop), so the fix is to
// STOP hand-rolling it, not to bolt an accessibilityRole onto the old box.
//
// RW-32/33/35: the rest of the scaffold's debt, measured the same way against
// the served build. The DOM carried two inputs whose only accessible name was a
// `placeholder` — which disappears the moment the field is focused — no
// `aria-live` on the error, no `aria-invalid`, and `autocomplete="on"`, which
// is r-n-w's default and tells a password manager nothing. The copy was
// hardcoded English reading "JackedLogs · web-rnw" / "react-native-web scaffold
// smoke": development strings, in production, on the first screen an athlete
// who reads in French ever sees. Every key used below already shipped
// translated in all five locales; this screen was simply the one surface that
// never called `useTranslation`. The label-above-the-field shape mirrors
// apps/mobile/src/screens/LoginScreen.tsx and CoachSettingsScreen.tsx, which
// already pair a visible <Text> with the same string as `accessibilityLabel`.
export function SignInForm() {
  const { t } = useTranslation();
  const { signIn } = useAuth();
  const { dark, family } = useThemePalette();
  const theme = resolveTheme(dark, family);
  const styles = makeStyles(theme);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async () => {
    setBusy(true);
    setError(null);
    try {
      await signIn(email.trim(), password);
    } catch (err) {
      const e = err as AuthError;
      // The key is only reachable through signInErrorKey: `toAuthError` always
      // supplies a `message`, so a bare `?? t(...)` would never fire and the
      // athlete would keep reading Cognito's English. Anything this does not
      // map still shows the provider's own copy — see RW-37.
      const key = signInErrorKey(e.code);
      setError(key !== null ? t(key) : (e.message ?? t("auth.signInFailed")));
      setBusy(false);
    }
  };

  return (
    <View style={styles.wrap}>
      <Text style={styles.title}>{t("mobile.login.title")}</Text>
      <Text style={styles.subtitle}>{t("mobile.login.subtitle")}</Text>

      <Text style={styles.label}>{t("auth.email")}</Text>
      <TextField
        theme={theme}
        accessibilityLabel={t("auth.email")}
        autoCapitalize="none"
        autoComplete="email"
        autoCorrect={false}
        editable={!busy}
        keyboardType="email-address"
        // The label carries the NAME; this carries the FORMAT, which is a
        // different job. Dropping it was an overcorrection: the defect was a
        // placeholder used AS the accessible name, not the placeholder itself.
        // Both siblings keep one (LoginScreen.tsx:93, CoachSettingsScreen.tsx:141).
        placeholder={t("auth.emailPlaceholder")}
        // Neither field can say WHICH half was wrong: Cognito answers one
        // opaque error for a bad pair. Marking both is the honest reading —
        // "one or both of these did not authenticate" — where marking one
        // would claim knowledge we do not have.
        invalid={error !== null}
        value={email}
        onChangeText={setEmail}
      />

      <Text style={styles.label}>{t("auth.password")}</Text>
      <TextField
        theme={theme}
        accessibilityLabel={t("auth.password")}
        // r-n-w defaults a TextInput to `autocapitalize="sentences"`, which on
        // a phone browser capitalises the first letter of a typed password.
        autoCapitalize="none"
        autoComplete="current-password"
        autoCorrect={false}
        editable={!busy}
        invalid={error !== null}
        // Punctuation, not copy: no locale has its own bullet, so this one
        // stays out of the i18n bundle on purpose.
        placeholder="•••••••••"
        secureTextEntry
        value={password}
        onChangeText={setPassword}
      />

      {error ? (
        // The one screen where failing is routine, so the failure has to reach
        // someone who is not looking at it. Same idiom as EditorTopBar.tsx:90,
        // the only other error line beside a submit in the repo.
        <Text style={styles.error} accessibilityLiveRegion="polite">
          {error}
        </Text>
      ) : null}

      <Button
        theme={theme}
        label={t("auth.signIn")}
        loading={busy}
        onPress={onSubmit}
      />
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    wrap: {
      width: 320,
      maxWidth: "100%",
      gap: s.s4,
    },
    title: {
      color: p.ink,
      fontSize: ty.sizes.xl,
      fontWeight: "700",
    },
    subtitle: {
      color: p.ink2,
      fontSize: ty.sizes.base,
      marginBottom: s.s3,
    },
    label: {
      color: p.ink2,
      // `base`, not the `sm` that CoachSettingsScreen's formLabel uses. The
      // sibling is a settings sub-form; this is the screen every session opens
      // with, and readability outranks density on the highest-frequency read
      // in the app. Raised on BOTH mounts in the same changeset — lifting only
      // the web would break the parity the rest of this screen just bought.
      fontSize: ty.sizes.base,
      fontWeight: "600",
      marginBottom: s.s2,
    },
    error: {
      color: p.danger,
      fontSize: ty.sizes.base,
    },
  });
}
